import { createContext, useContext, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box } from '@mui/material';
import LogoLoader from '../components/ui/LogoLoader';
import { api, apiErrorMessage } from './lib/api';

export interface PmaMe {
  user: { id: string; name: string; email?: string; avatarUrl?: string; title?: string; slug?: string };
  workspace: { id: string; name: string; slug: string; settings?: { allowClientAccess: boolean; screenshotIntervalSeconds: number; idleTimeoutMinutes: number } } | null;
  member: { role: string; permissions: string[] };
  permissionSections?: { key: string; label: string; permissions: string[] }[];
}

const PmaContext = createContext<PmaMe | null>(null);

/** Loads the current user's PMA identity (creating their workspace membership on first use). */
export function PmaProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['pma', 'me'],
    queryFn: async () => (await api.get<PmaMe>('/me')).data,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
        <LogoLoader />
      </Box>
    );
  }
  if (error || !data) {
    return <Alert severity="error">{apiErrorMessage(error, 'Could not load the project workspace.')}</Alert>;
  }
  return <PmaContext.Provider value={data}>{children}</PmaContext.Provider>;
}

export function usePmaMe(): PmaMe {
  const ctx = useContext(PmaContext);
  if (!ctx) throw new Error('usePmaMe must be used inside PmaProvider');
  return ctx;
}

export function usePermission() {
  const { member } = usePmaMe();
  const can = (...permissions: string[]) => permissions.every((p) => member.permissions.includes(p));
  const canAny = (...permissions: string[]) => permissions.some((p) => member.permissions.includes(p));
  return { can, canAny, role: member.role, permissions: member.permissions };
}
